import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Filter, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { WagerCard } from '@/components/wagers/WagerCard';
import { GAMES } from '@/lib/gameConfig';
import { api } from '@/lib/api';

const games = [
  { code: 'all', label: 'All Games' },
  ...GAMES.filter((g, i, arr) => arr.findIndex((x) => x.name === g.name) === i).map((g) => ({
    code: g.code,
    label: g.shortName,
  })),
];

const statuses = [
  { value: '', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'agreed,funding', label: 'Pending' },
  { value: 'locked,in_progress', label: 'Active' },
  { value: 'pending_verification,ready_to_collect', label: 'Completing' },
  { value: 'settled', label: 'Settled' },
];

export function WagersPage() {
  const [selectedGame, setSelectedGame] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [search, setSearch] = useState('');

  const { data: wagers, isLoading } = useQuery({
    queryKey: ['wagers', selectedGame, selectedStatus],
    queryFn: () =>
      api.getWagers({
        gameCode: selectedGame === 'all' ? undefined : selectedGame,
        status: selectedStatus || undefined,
      }),
  });

  const filteredWagers = wagers?.filter(
    (w) =>
      !search ||
      w.creator.displayName.toLowerCase().includes(search.toLowerCase()) ||
      w.gameCode.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6 pb-20 lg:pb-0">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold sm:text-3xl">Challenges</h1>
          <p className="text-muted-foreground">Browse and accept open challenges</p>
        </div>
        <Link to="/wagers/create">
          <Button variant="gradient" className="gap-2">
            <Plus className="h-4 w-4" />
            New Challenge
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search challenges..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {statuses.map((status) => (
                <Badge
                  key={status.value}
                  variant={selectedStatus === status.value ? 'default' : 'secondary'}
                  className="cursor-pointer"
                  onClick={() => setSelectedStatus(status.value)}
                >
                  {status.label}
                </Badge>
              ))}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {games.map((game) => (
              <Badge
                key={game.code}
                variant={selectedGame === game.code ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setSelectedGame(game.code)}
              >
                {game.label}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Challenge List */}
      {isLoading ? (
        <div className="flex h-60 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : filteredWagers && filteredWagers.length > 0 ? (
        <div className="space-y-4">
          {filteredWagers.map((wager) => (
            <WagerCard key={wager.id} wager={wager} showActions />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex h-60 flex-col items-center justify-center text-center">
            <Filter className="mb-4 h-12 w-12 text-muted-foreground" />
            <h3 className="font-semibold">No challenges found</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Try adjusting your filters or create a new challenge
            </p>
            <Link to="/wagers/create">
              <Button variant="outline" className="mt-4">
                New Challenge
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
